import {
  buildProjection,
  buildProjectionSeries,
  useApi,
  useSettlements
} from '@/api';
import {
  AsyncLinkCard,
  JustBarChart,
  JustLineChart,
  KpiCard,
  LinkButton
} from '@/components';
import { useChartFillDatasetsDates } from '@/hooks';
import type { SimpleChartData } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import {
  Box,
  CurrencyValue,
  Flex,
  formatCurrency,
  formatPercentage
} from '@blockanalitica/ui';

export default function RevenueCard() {
  const [data, error, isLoading] = useApi({
    url: '/overall/homepage-cards/'
  });
  const [settlements, settlementsError] = useSettlements();
  const projection = settlements ? buildProjection(settlements) : undefined;
  const projectedData = projection
    ? {
        projected_income: projection.income,
        projected_net_revenue: projection.net_revenue
      }
    : undefined;

  const incomeDatasets = [
    { xField: 'period', yField: 'income', backgroundColor: '#22c55e' }
  ];
  const netRevenueDatasets = [
    { xField: 'period', yField: 'net_revenue', backgroundColor: '#3b82f6' }
  ];
  const skyStakingDatasets = [
    { xField: 'date', yField: 'apy', borderColor: '#e4bc38' }
  ];

  const currencyChartConfig = {
    options: {
      aspectRatio: 2,
      scales: {
        y: {
          display: true,
          ticks: {
            callback: (value: number) => formatCurrency(value)
          }
        }
      },
      plugins: {
        tooltip: {
          display: true,
          callbacks: {
            label: (context) => formatCurrency(context.parsed.y)
          }
        }
      }
    }
  };

  const skyStakingChartConfig = {
    options: {
      aspectRatio: 2,
      scales: {
        y: {
          display: true,
          ticks: {
            callback: (value: number) => formatPercentage(value)
          }
        }
      },
      plugins: {
        tooltip: {
          display: true,
          callbacks: {
            label: (context) => formatPercentage(context.parsed.y)
          }
        }
      }
    }
  };

  const settlementsChartData = settlements
    ? (buildProjectionSeries(settlements, 3) as unknown as SimpleChartData[])
    : undefined;

  const skyStakingChartData = useChartFillDatasetsDates(
    data?.historic_data?.sky_staking,
    skyStakingDatasets,
    90
  );

  const kpiData = data?.kpi_data;

  // @ts-ignore
  const { VITE_PUBLIC_RESTRICTED_BUILD } = import.meta.env;
  const restrictedSection =
    VITE_PUBLIC_RESTRICTED_BUILD !== 'true' ? (
      <AsyncLinkCard
        to="/staking"
        hoverBorderColor="#fa43bd"
        error={error || !data}
        isLoading={isLoading}
        title="SKY Staking">
        <KpiCard
          title="APY"
          field="apy"
          padding="0"
          variant="linkCardKpi"
          data={kpiData}
          options={{
            percentage: true,
            variants: {
              kpiHeader: 'linkCardHeader'
            },
            valueOptions: {
              percentageValueOptions: {
                formatPercentageOptions: {
                  maximumFractionDigits: 2
                }
              }
            },
            valueTrendOptions: {
              percentageValueOptions: {
                formatPercentageOptions: {
                  maximumFractionDigits: 3
                }
              }
            }
          }}
        />

        <JustLineChart
          data={skyStakingChartData}
          datasets={skyStakingDatasets}
          config={skyStakingChartConfig}
        />

        <Flex flexDirection="column" gap="2xs" fontSize="-1">
          <Flex justifyContent="space-between">
            <Flex color="muted">Daily Distributions</Flex>
            <CurrencyValue
              value={kpiData?.daily_rewards}
              options={{
                currency: 'USD',
                iconSize: '0',
                notation: 'compact'
              }}
            />
          </Flex>
          <Flex justifyContent="space-between">
            <Flex color="muted">Yearly Distributions</Flex>
            <CurrencyValue
              value={kpiData?.yearly_rewards}
              options={{
                currency: 'USD',
                iconSize: '0',
                notation: 'compact'
              }}
            />
          </Flex>
        </Flex>

        <LinkButton to="/staking" variant="linkCard">
          Explore
        </LinkButton>
      </AsyncLinkCard>
    ) : null;

  return (
    <Flex gap="m" flexDirection={['column', 'row']}>
      <AsyncLinkCard
        to="/financials"
        hoverBorderColor="success"
        error={settlementsError}
        isLoading={!settlements && !settlementsError}
        title="Gross Revenue">
        <KpiCard
          title={
            <Flex flexDirection="column" gap="3xs">
              <Box>Projected Gross Revenue</Box>
            </Flex>
          }
          field="projected_income"
          padding="0"
          variant="linkCardKpi"
          data={projectedData}
          options={{
            currency: 'USD',
            notation: 'compact',
            variants: {
              kpiHeader: 'linkCardHeader'
            }
          }}
        />

        <JustBarChart
          data={settlementsChartData}
          datasets={incomeDatasets}
          config={currencyChartConfig}
        />

        <LinkButton to="/financials" variant="linkCard">
          Explore
        </LinkButton>
      </AsyncLinkCard>

      <AsyncLinkCard
        to="/financials"
        hoverBorderColor="success"
        error={settlementsError}
        isLoading={!settlements && !settlementsError}
        title="Net Revenue">
        <KpiCard
          title={
            <Flex flexDirection="column" gap="3xs">
              <Box>Projected Net Revenue</Box>
            </Flex>
          }
          field="projected_net_revenue"
          padding="0"
          variant="linkCardKpi"
          data={projectedData}
          options={{
            currency: 'USD',
            notation: 'compact',
            variants: {
              kpiHeader: 'linkCardHeader'
            }
          }}
        />

        <JustBarChart
          data={settlementsChartData}
          datasets={netRevenueDatasets}
          config={currencyChartConfig}
        />

        <LinkButton to="/financials" variant="linkCard">
          Explore
        </LinkButton>
      </AsyncLinkCard>

      {restrictedSection}
    </Flex>
  );
}
